import React, { FC, useEffect, useState } from 'react';
import { Button, DialogActions, DialogContent, FormControl, Grid, TextField, Box } from '@mui/material';
import { useMutation, useQuery } from '@apollo/client';
import { getRolesTemplate } from 'apollo/templates/user/query.graphql';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { modifyUserTemplate } from 'apollo/templates/user/mutation.graphql';
import { Organization } from 'models/organization';
import { ClinicDropdown, LinkedOrganization, User } from 'models/user';
import AuthMethodFields, {
    authMethodVariables,
    validateAuthMethodFields,
} from 'components/users/shared/auth-method-fields/auth-method-fields.component';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import { permissions } from 'utils/permission';
import { nameRegExp } from 'config/regex';
import LinkedOrganizationList from '../linked-organizations/linked-organizations';

type UpdateUserFormProps = {
    onDialogClose: () => void;
    userToUpdate: User;
    onUserUpdated: (user: User) => void;
    refetchUsers?: () => void;
};
const UpdateUserForm: FC<UpdateUserFormProps> = ({
    onDialogClose,
    userToUpdate,
    onUserUpdated,
    refetchUsers,
}: UpdateUserFormProps) => {
    const { t: translate } = useTranslation(['common', 'users', 'organization']);
    const [isFirstNameValid, setIsFirstNameValid] = useState<boolean>(false);
    const [isLastNameValid, setIsLastNameValid] = useState<boolean>(false);
    const { showBoundary } = useDenthubErrorBoundary();

    const [modifyUser] = useMutation(modifyUserTemplate, {
        onCompleted: ({ modifyUser }) => {
            onUserUpdated(modifyUser);
            refetchUsers && refetchUsers();
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const { data: { roles } = {}, loading: rolesLoading } = useQuery(getRolesTemplate, {
        notifyOnNetworkStatusChange: true,
        variables: { type: permissions.isDentist(userToUpdate.type) ? 'DENTIST' : 'TECHNICIAN' },
        onError(error) {
            showBoundary(error);
        },
    });

    const validate = (values: Partial<User>) => {
        const errors: Record<string, string> = {};
        if (!values.firstName) {
            errors.firstName = translate('users:VALIDATION:FIRST_NAME');
        }
        if (values.firstName && !isFirstNameValid) {
            errors.firstName = translate('common:VALIDATION.INCORRECT_NAME');
        }
        if (!values.lastName) {
            errors.lastName = translate('users:VALIDATION:LAST_NAME');
        }
        if (values.lastName && !isLastNameValid) {
            errors.lastName = translate('common:VALIDATION.INCORRECT_NAME');
        }
        if (!values.email) {
            errors.email = translate('users:VALIDATION:EMAIL');
        }
        if (!values.linkedOrganizations || values.linkedOrganizations.length === 0) {
            errors.linkedOrganizations = translate('users:VALIDATION:LINKED_ORGANIZATIONS');
        }
        validateAuthMethodFields(values, translate, errors, { currentMethod: userToUpdate.authMethod });
        if (permissions.isDentist(userToUpdate.type) && !values.defaultClinic) {
            errors.defaultClinic = translate('organization:VALIDATION:DEFAULT_CLINIC');
        }
        return errors;
    };

    const formik = useFormik({
        initialValues: {
            _id: userToUpdate._id,
            title: userToUpdate.title,
            firstName: userToUpdate.name.firstName,
            lastName: userToUpdate.name.lastName,
            email: userToUpdate.email,
            linkedOrganizations: userToUpdate.linkedOrganizations,
            organization: userToUpdate.organization as Organization,
            ...(permissions.isDentist(userToUpdate.type)
                ? {
                      defaultClinic: {
                          _id: (userToUpdate.defaultClinic as Organization)?._id,
                          name: (userToUpdate.defaultClinic as Organization)?.name,
                      } as Organization,
                  }
                : {}),
            // DD-3552: lab users get a login method too, not only dentists.
            enableUserSync: userToUpdate.enableUserSync || false,
            authMethod: userToUpdate.authMethod,
            syncid: userToUpdate.syncid,
            personalIdentityNumber: userToUpdate.personalIdentityNumber ?? '',
        },
        validateOnMount: true,
        validate,
        enableReinitialize: true,
        onSubmit: async (values) => {
            const user = {
                _id: values._id,
                name: { firstName: values.firstName, lastName: values.lastName },
                organization: (values.organization as Organization)?._id,
                email: values.email,
                linkedOrganizations: (values.linkedOrganizations || []).map((linkedOrganization) => {
                    return {
                        organization: (linkedOrganization.organization as Organization)._id,
                        role: linkedOrganization.role,
                        isWhiteLabeled: (linkedOrganization.organization as Organization).isWhiteLabeled,
                        active: linkedOrganization.active,
                        type: linkedOrganization.type,
                    };
                }),
                ...authMethodVariables(values),
                ...(permissions.isDentist(userToUpdate.type)
                    ? {
                          defaultClinic: (values.defaultClinic as ClinicDropdown)?._id,
                          title: values.title,
                      }
                    : {}),
            };
            await modifyUser({ variables: { user } });
            onDialogClose();
        },
    });

    useEffect(() => {
        if (userToUpdate) {
            setIsFirstNameValid(nameRegExp.test(userToUpdate.name?.firstName || ''));
            setIsLastNameValid(nameRegExp.test(userToUpdate.name?.lastName || ''));
            formik.validateForm();
        }
    }, [userToUpdate]);

    const handleFirstNameChange = (newFirstName: string) => {
        const firstName = newFirstName.charAt(0).toUpperCase() + newFirstName.slice(1);
        setIsFirstNameValid(nameRegExp.test(firstName));
        formik.setFieldValue('firstName', firstName);
    };

    const handleLastNameChange = (newLastName: string) => {
        const lastName = newLastName.charAt(0).toUpperCase() + newLastName.slice(1);
        setIsLastNameValid(nameRegExp.test(lastName));
        formik.setFieldValue('lastName', lastName);
    };

    const updateLinkedOrganization = (organization: LinkedOrganization, changes: Partial<LinkedOrganization>) => {
        const updatedLinkedOrganizations = (formik.values.linkedOrganizations || []).map((lOrg) => {
            if ((lOrg.organization as Organization)._id === (organization.organization as Organization)._id) {
                return { ...lOrg, ...changes };
            }
            return lOrg;
        });
        formik.setFieldValue('linkedOrganizations', updatedLinkedOrganizations);
    };

    const handleLOrgActiveChange = (organization: LinkedOrganization, isActive: boolean) => {
        updateLinkedOrganization(organization, { active: isActive });
    };

    const handleLOrgRoleChange = (organization: LinkedOrganization, role: string) => {
        updateLinkedOrganization(organization, { role });
    };

    return (
        <form onSubmit={formik.handleSubmit}>
            <DialogContent>
                <Box mt={1}>
                    <Grid container spacing={1}>
                        <Grid item xs={6}>
                            <FormControl margin="dense" fullWidth>
                                <TextField
                                    id="first-name"
                                    label={translate('common:FIRST_NAME')}
                                    margin="dense"
                                    required
                                    helperText={formik.touched.firstName && formik.errors.firstName}
                                    error={formik.touched.firstName && Boolean(formik.errors.firstName)}
                                    onChange={(e) => handleFirstNameChange(e.target.value)}
                                    onBlur={formik.getFieldProps('firstName').onBlur}
                                    name={formik.getFieldProps('firstName').name}
                                    value={formik.getFieldProps('firstName').value}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                            <FormControl margin="dense" fullWidth>
                                <TextField
                                    id="last-name"
                                    label={translate('common:LAST_NAME')}
                                    margin="dense"
                                    required
                                    helperText={formik.touched.lastName && formik.errors.lastName}
                                    error={formik.touched.lastName && Boolean(formik.errors.lastName)}
                                    onChange={(e) => handleLastNameChange(e.target.value)}
                                    onBlur={formik.getFieldProps('lastName').onBlur}
                                    name={formik.getFieldProps('lastName').name}
                                    value={formik.getFieldProps('lastName').value}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                            <FormControl margin="dense" fullWidth>
                                <TextField
                                    id="email"
                                    label={translate('common:EMAIL')}
                                    margin="dense"
                                    required
                                    type="email"
                                    disabled
                                    helperText={formik.touched.email && formik.errors.email}
                                    error={formik.touched.email && Boolean(formik.errors.email)}
                                    {...formik.getFieldProps('email')}
                                />
                            </FormControl>
                        </Grid>
                        <AuthMethodFields formik={formik} currentMethod={userToUpdate.authMethod} />

                        {permissions.isDentist(userToUpdate.type) && (
                            <Grid item xs={6}>
                                <FormControl margin="dense" fullWidth>
                                    <TextField
                                        id="title"
                                        label={translate('common:TITLE')}
                                        helperText={formik.touched.title && formik.errors.title}
                                        error={formik.touched.title && Boolean(formik.errors.title)}
                                        {...formik.getFieldProps('title')}
                                    />
                                </FormControl>
                            </Grid>
                        )}
                    </Grid>
                    {(formik.values.linkedOrganizations || []).length > 0 && !rolesLoading && (
                        <Grid container spacing={1}>
                            <Grid item xs={12} mt={2} mb={1}>
                                <LinkedOrganizationList
                                    linkedOrganizations={formik.values.linkedOrganizations || []}
                                    roles={roles || []}
                                    onActiveChange={handleLOrgActiveChange}
                                    onRoleChange={handleLOrgRoleChange}
                                />
                            </Grid>
                        </Grid>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onDialogClose}>{translate('common:CANCEL')}</Button>
                <Button disabled={!formik.isValid} variant="contained" type="submit">
                    {translate('common:SAVE')}
                </Button>
            </DialogActions>
        </form>
    );
};

export default UpdateUserForm;
